import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

export const Route = createFileRoute('/tennis-league-software')({
    component: RouteComponent,
})

const FEATURES = [
    {code: "BX", title: "Box leagues", desc: "Split members into boxes of four to six, then promote and relegate each round."},
    {code: "LD", title: "Ladder leagues", desc: "Let members challenge players above them and climb the rankings."},
    {code: "SD", title: "Singles & doubles", desc: "Run singles, doubles or mixed doubles competitions from the same club."},
    {code: "ST", title: "Set-by-set scores", desc: "Record results the way tennis is scored, including tie-breaks."},
    {code: "CT", title: "Court booking slots", desc: "Give each fixture a court and time so members know where to play."},
    {code: "RK", title: "Ratings & standings", desc: "Track club rankings and publish standings across the season."},
];

const LEAGUE_TYPES = [
    {name: "Box Leagues", sub: "Small groups with promotion and relegation", to: "/round-robin-tournament-generator/"},
    {name: "Ladder Leagues", sub: "Challenges and ranking movement", to: "/ladder-league-generator/"},
    {name: "Tennis Tournaments", sub: "Draws, seeds and knockout rounds", to: "/tennis-tournament-software/"},
    {name: "Sports League Management", sub: "Recurring fixtures and standings", to: "/sports-league-management-software/"},
];

const BENEFITS = [
    {title: "Members arrange their own matches", desc: "Players see who they owe a match and organise a time between them."},
    {title: "Rating bands stay separate", desc: "Keep beginner, improver, intermediate and open groups in their own divisions."},
    {title: "Standings members can trust", desc: "Results feed straight into rankings, so nobody has to chase the organiser."},
];

const USE_CASES = [
    {title: "For Tennis Clubs", desc: "Run member box leagues and ladders through the summer season."},
    {title: "For Coaches", desc: "Give squads competitive match play between coaching sessions."},
    {title: "For Doubles Groups", desc: "Organise recurring doubles rounds with rotating partners."},
    {title: "For Rating Divisions", desc: "Separate players by standard so matches stay competitive."},
    {title: "For Winter Leagues", desc: "Keep indoor competitions running across limited court time."},
    {title: "For Social Play", desc: "Give casual members structure without a full tournament."},
];

const COMPARE = [
    {need: "Box rounds", manual: "Rebuilt by hand every six weeks", scorepal: "Boxes generated with promotion and relegation"},
    {need: "Ladder movement", manual: "Manual ranking edits after each challenge", scorepal: "Rankings move automatically with each result"},
    {need: "Score reporting", manual: "Results texted to the organiser", scorepal: "Players submit set scores against their fixture"},
    {need: "Court allocation", manual: "Noticeboard and guesswork", scorepal: "Each match carries its court and time"},
    {need: "Member experience", manual: "Members ask who they play next", scorepal: "Fixtures, standings and rankings on a public page"},
];

const FAQS = [
    {
        q: 'What is tennis league software?',
        a: <>Tennis league software helps clubs run recurring competitions such as box leagues and ladders — organising fixtures, collecting set scores, publishing standings and tracking rankings across a season.</>,
    },
    {
        q: 'Can ScorePal run tennis box leagues?',
        a: <>Yes. Members can be split into boxes where everyone plays everyone, with results feeding a table you use to promote and relegate players between rounds.</>,
    },
    {
        q: 'Does ScorePal support ladder leagues?',
        a: <>Yes. ScorePal supports <Link to={"/ladder-league-generator/" as any} className="text-[#082F8A] underline font-semibold">ladder-style</Link> tennis leagues where players challenge others above them and move up or down the rankings.</>,
    },
    {
        q: 'Can scores be recorded set by set?',
        a: <>Yes. Tennis results are entered as sets, so a 6-4 3-6 10-8 scoreline is stored the way it was actually played rather than as a single number.</>,
    },
    {
        q: 'Is this different from tennis tournament software?',
        a: <>Yes. Tennis league software is built for recurring club play across a season. <Link to={"/tennis-tournament-software/" as any} className="text-[#082F8A] underline font-semibold">Tennis tournament software</Link> is built for one-off events with draws, seeds and knockout rounds.</>,
    },
];

function RouteComponent() {
    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>Tennis League Software for Clubs & Organisers | ScorePal</title>
            <meta name="description" content="Run tennis leagues with ScorePal. Manage box leagues, ladders, singles and doubles fixtures, set-by-set scores, club rankings and season standings from one platform." />
            <link rel="canonical" href={`${config.publicUrl}/tennis-league-software/`} />
            <meta property="og:title" content="Tennis League Software | ScorePal" />
            <meta property="og:description" content="Create tennis box leagues and ladders, collect set scores and publish standings with ScorePal." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${config.publicUrl}/tennis-league-software/`} />
            <meta property="og:image" content="https://scorepal.co/images/tennis-league-software-og.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Tennis League Software | ScorePal" />
            <meta name="twitter:description" content="Run tennis box leagues, ladders, rankings and standings from one platform." />

            <JsonLd id={`page${Route.id}/app`} item={{
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "ScorePal Tennis League Software",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "Web",
                "description": "Tennis league software for managing box leagues, ladders, singles and doubles fixtures, set scores, standings and club rankings.",
                "url": "https://scorepal.co/tennis-league-software/",
                "publisher": {"@type": "Organization", "name": "ScorePal"},
            }} />
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    {"@type": "Question", "name": "What is tennis league software?", "acceptedAnswer": {"@type": "Answer", "text": "Tennis league software helps clubs run recurring competitions such as box leagues and ladders, organising fixtures, collecting set scores, publishing standings and tracking rankings across a season."}},
                    {"@type": "Question", "name": "Can ScorePal run tennis box leagues?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Members can be split into boxes where everyone plays everyone, with results feeding a table you use to promote and relegate players between rounds."}},
                    {"@type": "Question", "name": "Does ScorePal support ladder leagues?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal supports ladder-style tennis leagues where players challenge others above them and move up or down the rankings."}},
                    {"@type": "Question", "name": "Can scores be recorded set by set?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Tennis results are entered as sets, so a 6-4 3-6 10-8 scoreline is stored the way it was actually played rather than as a single number."}},
                    {"@type": "Question", "name": "Is this different from tennis tournament software?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Tennis league software is built for recurring club play across a season, while tennis tournament software is built for one-off events with draws, seeds and knockout rounds."}},
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Tennis League Software", item: "https://scorepal.co/tennis-league-software/"}]} />

            {/* Hero */}
            <section className="relative overflow-hidden p-4">
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    Tennis League Software
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(38px,5.5vw,68px)] leading-none font-bold tracking-wide mb-5">
                                    Run club box leagues and ladders without the noticeboard
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    ScorePal helps tennis clubs and organisers run box leagues, ladders, singles and doubles
                                    competitions — with set-by-set scores, court allocation, club rankings and standings members
                                    can check for themselves.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Get started - it's free</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </Link>
                                    <Link to={"/tennis-tournament-software/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        Running a Tournament?
                                    </Link>
                                </div>
                            </div>

                            {/* Live box league card */}
                            <div className="bg-white/5 border border-white/10 rounded-3xl p-6 lg:p-8 backdrop-blur-sm">
                                <h3 className="font-bebas text-white text-2xl font-bold tracking-wide mb-2">Live box league standings</h3>
                                <p className="font-inter text-white/70 text-sm mb-5">Members see where they sit and who they still owe a match, round by round.</p>
                                <div className="rounded-2xl bg-white/10 p-3">
                                    <div className="grid grid-cols-[28px_1fr_44px_44px] items-center gap-2 pb-2 border-b border-white/15 font-inter text-white/50 text-xs uppercase tracking-wide">
                                        <span>#</span><span>Player</span><span className="text-center">P</span><span className="text-right">Pts</span>
                                    </div>
                                    {[
                                        {pos: 1, name: "A. Whitfield", played: "5", pts: "42"},
                                        {pos: 2, name: "M. Serrano", played: "5", pts: "38"},
                                        {pos: 3, name: "L. Brennan", played: "4", pts: "29"},
                                        {pos: 4, name: "K. Osei", played: "4", pts: "24"},
                                    ].map((row, i, arr) => (
                                        <div key={row.pos} className={`grid grid-cols-[28px_1fr_44px_44px] items-center gap-2 py-3 ${i < arr.length - 1 ? "border-b border-white/10" : ""}`}>
                                            <span className="font-inter text-white/60 text-sm">{row.pos}</span>
                                            <strong className="font-inter text-white text-sm">{row.name}</strong>
                                            <span className="font-inter text-white/70 text-sm text-center">{row.played}</span>
                                            <strong className="font-inter text-white text-sm text-right">{row.pts}</strong>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="py-16 lg:py-24 bg-white scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Everything you need to run a tennis league
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Replace the clubhouse noticeboard, the spreadsheet of boxes and the results texted to one volunteer with software built for club tennis.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {FEATURES.map((f) => (
                            <div key={f.title} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <div className="w-11 h-11 rounded-xl bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">{f.code}</div>
                                <h3 className="font-inter text-[#1a1a2e] font-semibold text-lg mb-2">{f.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* League types */}
            <section id="league-types" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Support for every type of tennis league
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Club tennis runs on boxes, ladders and doubles rounds. ScorePal handles each of them, and the seasons that string them together.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {LEAGUE_TYPES.map((f) => (
                            <Link key={f.name} to={f.to as any} className="group bg-white border border-[#e5e7eb] rounded-2xl p-5 hover:-translate-y-1 hover:border-[#082F8A]/25 transition">
                                <div className="font-inter font-bold text-[#1a1a2e] group-hover:text-[#082F8A]">{f.name}</div>
                                <div className="font-inter text-[#1a1a2e]/60 text-sm mt-1">{f.sub}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Club play + mock app */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div>
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                            Designed for how club tennis actually runs
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/70 text-base leading-relaxed mb-6">
                            Club leagues are played around work, weather and court availability. Members arrange matches
                            between themselves, so the software has to make it obvious who owes whom a match — and keep
                            the table honest once the score comes in.
                        </p>
                        <div className="space-y-4">
                            {BENEFITS.map((b) => (
                                <div key={b.title} className="flex gap-3">
                                    <CheckBadgeIcon size={24} />
                                    <div>
                                        <strong className="font-inter text-[#1a1a2e]">{b.title}</strong>
                                        <p className="font-inter text-[#1a1a2e]/70 text-sm">{b.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Mock app */}
                    <div className="rounded-3xl bg-linear-to-br from-[#eef4ff] to-white border border-[#e5e7eb] p-6 lg:p-8 shadow-sm">
                        <div className="bg-white border border-[#e5e7eb] rounded-2xl p-5">
                            {[
                                {l: "Summer Box League — Round 3", r: "Active", badge: "live"},
                                {l: "Your next match", r: "vs M. Serrano"},
                                {l: "Court", r: "Court 4 · Thursday 18:00"},
                                {l: "Last result", r: "6-4 3-6 10-8"},
                                {l: "Box leader", r: "A. Whitfield", badge: "gold"},
                            ].map((row, i, arr) => (
                                <div key={row.l} className={`flex items-center justify-between gap-3 py-3.5 font-inter text-sm ${i < arr.length - 1 ? "border-b border-[#eef0f4]" : ""}`}>
                                    <span className="text-[#1a1a2e]/70">{row.l}</span>
                                    {row.badge === "live"
                                        ? <span className="rounded-full bg-[#ecfdf3] text-[#12b76a] px-2.5 py-1 text-xs font-bold">{row.r}</span>
                                        : row.badge === "gold"
                                            ? <span className="rounded-full bg-[#fffbeb] text-[#b54708] px-2.5 py-1 text-xs font-bold">{row.r}</span>
                                            : <strong className="text-[#1a1a2e] text-right">{row.r}</strong>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Comparison */}
            <section id="comparison" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        ScorePal vs spreadsheets for tennis leagues
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-8">
                        A box league spreadsheet works until members start asking who they still owe a match, and every round has to be rebuilt by hand.
                    </p>
                    <div className="overflow-x-auto">
                        <div className="min-w-[680px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-3 bg-[#F6F7FB] font-bebas text-lg">
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Need</div>
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Spreadsheets</div>
                                <div className="p-5 text-[#082F8A]">ScorePal</div>
                            </div>
                            {COMPARE.map((row, i) => (
                                <div key={row.need} className={`grid grid-cols-3 border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-5 border-r border-[#d9dce7] font-semibold">{row.need}</div>
                                    <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]/70">{row.manual}</div>
                                    <div className="p-5 font-medium">{row.scorepal}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Use cases */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Built for clubs, coaches and member competitions
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        From a summer box league to a year-round ladder, ScorePal keeps club tennis organised across every standard of player.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {USE_CASES.map((u) => (
                            <div key={u.title} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <h3 className="font-inter text-[#082F8A] font-semibold text-base mb-2">{u.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{u.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA card → tournament software */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="rounded-3xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-center">
                    <div>
                        <h2 className="font-bebas text-white uppercase text-[clamp(26px,3.5vw,40px)] leading-tight font-bold tracking-wide mb-4">
                            Running a club championship or open?
                        </h2>
                        <p className="font-inter text-white/80 text-base leading-relaxed mb-6">
                            This page is about recurring club leagues. If you are running a championship weekend with
                            draws, seeds, consolation rounds and finals, see our tennis tournament software page.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to={"/tennis-tournament-software/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                                <span className="font-inter text-base font-medium">View Tournament Software</span>
                                <div className="bg-[#082F8A] rounded-full p-1">
                                    <ArrowUpRightIcon className="text-white" size={28} />
                                </div>
                            </Link>
                            <Link to={"/signup/player/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                Get started - it's free
                            </Link>
                        </div>
                    </div>
                    <div className="bg-white/10 rounded-2xl p-6">
                        <h3 className="font-inter text-white font-semibold mb-3">Popular tournament features</h3>
                        <ul className="list-disc pl-5 space-y-1.5 font-inter text-sm text-white/85">
                            <li>Knockout draws</li>
                            <li>Seeding</li>
                            <li>Consolation rounds</li>
                            <li>Singles & doubles events</li>
                            <li>Finals day scheduling</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="container mx-auto px-6 lg:px-12 pb-8 lg:pb-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Run your next tennis league with ScorePal
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                        Box leagues, ladders, singles and doubles fixtures, set-by-set scores and club rankings — tennis league software built for modern clubs.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                        {["Box Leagues", "Ladder Leagues", "Singles & Doubles", "Set Scores", "Court Slots", "Club Rankings"].map((pill) => (
                            <span key={pill} className="rounded-full bg-white/10 px-4 py-2 font-inter text-sm font-medium text-white/90">{pill}</span>
                        ))}
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"Tennis League Software FAQs"} faqs={FAQS} />
            <FooterSection />
        </div>
    );
}
